import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export type Zone = 'wealth' | 'trade';

const ZONE_COLORS = {
  wealth: '#10b981',
  trade: '#3b82f6',
};

const ZONE_NAMES = {
  wealth: 'Wealth AI',
  trade: 'Trade AI',
};

// Route prefix → zone mapping
const ROUTE_ZONE_MAP: Record<string, Zone> = {
  '/dashboard': 'wealth',
  '/fire-calculator': 'wealth',
  '/lifecycle': 'wealth',
  '/net-worth': 'wealth',
  '/dca-tool': 'wealth',
  '/bucket-visualizer': 'wealth',
  '/five-pillars': 'wealth',
  '/playbook': 'wealth',
  '/book': 'wealth',
  '/options-research': 'trade',       // canonical name per 2026-07-22 vocab migration D3
  '/trade-signals': 'trade',          // legacy - backwards compat; redirects to /options-research
  '/options-equity-signals': 'trade', // legacy - backwards compat; redirects to /options-research
  '/stock-research': 'trade',         // canonical name per 2026-07-22 vocab migration D3
  '/stock-signals': 'trade',          // legacy - backwards compat; dual-mounted with /stock-research
  '/equity-signals': 'trade',         // legacy - backwards compat; dual-mounted
  '/spx': 'trade',
  '/spx/v2': 'trade',
  '/indices': 'trade',
  '/futures': 'trade',
  '/algo': 'trade',
  // Notches preview namespace - all under the Trade zone shell
  '/notches/dashboard': 'trade',
  '/notches': 'trade',
  '/markets': 'trade',  // Notches Markets pillar (coming-soon placeholder for now)
  '/boring-bucket': 'trade',  // The Boring Bucket — mentored membership section
  '/risk': 'trade',           // Portfolio Risk Analyzer — BB-only (Phase A)
};

// Neutral routes that don't change zone
const NEUTRAL_ROUTES = ['/profile', '/upgrade', '/help', '/contact-support', '/email-info'];

interface ZoneContextType {
  zone: Zone;
  setZone: (z: Zone) => void;
  zoneColor: string;
  zoneName: string;
}

const ZoneContext = createContext<ZoneContextType>({
  zone: 'wealth',
  setZone: () => {},
  zoneColor: ZONE_COLORS.wealth,
  zoneName: ZONE_NAMES.wealth,
});

export const ZoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [zone, setZoneState] = useState<Zone>(() => {
    return (localStorage.getItem('active-zone') as Zone) || 'wealth';
  });

  const setZone = (z: Zone) => {
    setZoneState(z);
    localStorage.setItem('active-zone', z);
  };

  // Auto-detect zone from route
  useEffect(() => {
    const path = location.pathname;
    if (NEUTRAL_ROUTES.some(r => path.startsWith(r))) return;
    for (const [prefix, z] of Object.entries(ROUTE_ZONE_MAP)) {
      if (path.startsWith(prefix)) {
        if (z !== zone) setZone(z);
        return;
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set CSS custom properties — different background per zone
  useEffect(() => {
    document.documentElement.style.setProperty('--zone-color', ZONE_COLORS[zone]);
    document.documentElement.style.setProperty('--zone-color-dim', ZONE_COLORS[zone] + '30');
    // Wealth AI = lighter warm background, Trade AI = dark blue
    // Wealth AI = warm dark gray-green | Trade AI = deep dark blue
    const bg = zone === 'wealth'
      ? 'linear-gradient(135deg, #1a2520 0%, #1e3028 50%, #1a2520 100%)'
      : 'linear-gradient(135deg, #0f172a 0%, #152040 50%, #0f172a 100%)';
    document.documentElement.style.setProperty('--zone-text', '#e2e8f0');
    document.documentElement.style.setProperty('--zone-text-muted', '#94a3b8');
    document.documentElement.style.setProperty('--zone-card', zone === 'wealth' ? '#1f2d26' : '#1e293b');
    document.documentElement.style.setProperty('--zone-card-border', zone === 'wealth' ? '#2d4a3a' : '#334155');
    // Nav matches the page — lighter shade of the zone color
    document.documentElement.style.setProperty('--zone-topbar-bg', zone === 'wealth' ? '#1e2b24' : '#131c2e');
    document.documentElement.style.setProperty('--zone-topbar-border', zone === 'wealth' ? '#2a3d32' : '#1e2d4a');
    document.documentElement.style.setProperty('--zone-sidebar-bg', zone === 'wealth' ? '#1b2822' : '#111827');
    document.documentElement.style.setProperty('--zone-sidebar-border', zone === 'wealth' ? '#2a3d32' : '#1e293b');
    document.documentElement.style.setProperty('--zone-sidebar-text', '#94a3b8');
    document.documentElement.style.setProperty('--zone-sidebar-hover', zone === 'wealth' ? '#243530' : '#1e293b');
    // Add body class for zone-specific CSS overrides
    document.body.classList.toggle('zone-wealth', zone === 'wealth');
    document.body.classList.toggle('zone-trade', zone === 'trade');
    document.documentElement.style.setProperty('--zone-bg', bg);
  }, [zone]);

  return (
    <ZoneContext.Provider value={{
      zone,
      setZone,
      zoneColor: ZONE_COLORS[zone],
      zoneName: ZONE_NAMES[zone],
    }}>
      {children}
    </ZoneContext.Provider>
  );
};

export const useZone = () => useContext(ZoneContext);
